import Button from '@orn/shared-web/components/Button/Button';
import MessagingCard from '@orn/shared-web/components/MessagingCard';
import { ArrowNarrowLeftIcon, ZapIcon } from '@orn/shared-web/icons';

import onboardingCelebrationIllustration from '../../assets/onboarding-celebration.svg';
import { useCurrentUser } from '../../hooks/queries/useCurrentUser';

import type { IOnboardingStepProps } from './types';
import type { ReactNode } from 'react';

interface IBaseOnboardingMilestoneStepProps {
  /** Big-serif headline above the body copy. Pre-interpolated — the shell does no name lookup. */
  title: string;
  /** Body paragraph below the title. */
  subtitle: string;
  /** Optional info-card slot rendered between the subtitle and the CTA. */
  card?: ReactNode;
  /** CTA label. Defaults to "Continue". */
  ctaLabel?: string;
  /** Advance to the next step. */
  onNext: () => void;
  /**
   * Inline back-arrow handler, rendered above the illustration. Required —
   * every milestone screen sits past the first wizard step, so the
   * orchestrator always has a previous screen to return to.
   */
  onBack: () => void;
}

/**
 * Shared shell for the celebratory interstitial screens in the onboarding
 * flow. Pure presentational — title and subtitle arrive pre-interpolated; the
 * only state it owns is "which CTA was clicked." Composes:
 *
 *   [back arrow] → centered illustration → big-serif title → body copy
 *   → optional info card → full-width primary CTA
 *
 * The illustration source is intentionally hardcoded: every milestone shares
 * the same `onboarding-celebration.svg` fireworks asset, so callers don't
 * have to thread it through. Concrete consumers (`GreatStartStep`,
 * `AlmostThereStep`) live alongside in this file.
 */
const BaseOnboardingMilestoneStep = ({
  title,
  subtitle,
  card,
  ctaLabel = 'Continue',
  onNext,
  onBack,
}: IBaseOnboardingMilestoneStepProps) => {
  return (
    <div className="flex w-full flex-col items-start gap-6 pb-10">
      <Button
        color="tertiary"
        size="md"
        iconLeading={ArrowNarrowLeftIcon}
        onClick={onBack}
        aria-label="Go back"
        className="-ml-2.5"
      />
      <div className="flex w-full flex-col items-center justify-center pb-2">
        <img src={onboardingCelebrationIllustration} alt="" aria-hidden className="h-[110px] w-[112px] shrink-0" />
      </div>
      <div className="flex w-full flex-col gap-3">
        <h1 className="text-primary font-arizona text-display-md w-full tracking-[-0.72px]">{title}</h1>
        <p className="text-secondary text-md w-full font-medium">{subtitle}</p>
      </div>
      {card && <div className="w-full">{card}</div>}
      <Button color="primary" size="xl" className="w-full" onClick={onNext}>
        {ctaLabel}
      </Button>
    </div>
  );
};

BaseOnboardingMilestoneStep.displayName = 'BaseOnboardingMilestoneStep';

export default BaseOnboardingMilestoneStep;

/**
 * Celebratory transition screen shown after the user finishes the "About You"
 * phase and before they begin entering Loved One(s) information. Reads the
 * user's `preferredName` (with `firstName` as fallback) off the current
 * user for the headline interpolation, then defers all rendering to
 * {@link BaseOnboardingMilestoneStep}.
 *
 * Figma: Mantle - UX/UI, node 4154:67409 "Onboarding Step 05".
 */
export const GreatStartStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { user } = useCurrentUser();
  const firstName = user?.firstName ?? '';
  const preferredName = user?.preferredName ?? '';
  const displayName = preferredName.trim() || firstName;

  return (
    <BaseOnboardingMilestoneStep
      title={`Great start${displayName ? `, ${displayName}` : ''}!`}
      subtitle="You're all set with your account. Now let's learn about your loved one's finances."
      onNext={onNext}
      onBack={onBack ?? (() => undefined)}
    />
  );
};

GreatStartStep.displayName = 'GreatStartStep';

/**
 * Transitional celebration screen rendered after the family-details sub-steps,
 * just before connecting accounts. Defers all rendering to
 * {@link BaseOnboardingMilestoneStep}, contributing only the inline "Quick
 * Setup" info card.
 *
 * Figma: Mantle - UX/UI, node 4174:72005 "Onboarding Step 14".
 */
export const AlmostThereStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  return (
    <BaseOnboardingMilestoneStep
      title="Almost there!"
      subtitle="Just two more steps — connect accounts and set up your subscription. Once you're all set, your personalized dashboard will be ready."
      card={
        <MessagingCard variant="mist" className="px-4 py-4">
          <div className="flex items-start gap-2">
            <span className="text-secondary flex size-4 shrink-0 items-center justify-center pt-1 [&>svg]:size-4">
              <ZapIcon />
            </span>
            <div className="flex flex-col">
              <p className="text-secondary text-md font-semibold leading-6">Quick Setup</p>
              <p className="text-secondary text-sm font-normal leading-5">Average setup time: 3 minutes</p>
            </div>
          </div>
        </MessagingCard>
      }
      onNext={onNext}
      onBack={onBack ?? (() => undefined)}
    />
  );
};

AlmostThereStep.displayName = 'AlmostThereStep';
